import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calculator, 
  Zap, 
  TrendingDown,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Star,
  Sparkles,
  Shield,
  Users,
  Ruler,
  PaintBucket,
  Home,
  Hammer
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { ChatInput, ChatCard, MaterialCard, UpgradePrompt } from '../components/features';
import { ChatCardSkeleton } from '../components/ui';
// Scroll animation hook available for future use
// import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useHasAnimated } from '../hooks/useHasAnimated';
import ChatView from './ChatView';

const HomePage = () => {
  const { user } = useAuth();
  const { 
    chats, 
    isLoading, 
    getRemainingCalcs, 
    canCalculate, 
    sendMessage, 
    deleteChat,
    activeChat,
    setActiveChat,
    isPro
  } = useChat();
  const navigate = useNavigate();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const hasAnimated = useHasAnimated();
  
  // Trust signals data - genuine features, no fake numbers
  const trustSignals = [
    { icon: Shield, text: 'Industry-Standard Formulas', color: 'text-green-600' },
    { icon: Zap, text: 'Instant AI Calculations', color: 'text-blue-600' },
    { icon: CheckCircle, text: 'Built-in Waste Factors', color: 'text-purple-600' },
  ];

  const remainingCalcs = getRemainingCalcs();

  const handleSendMessage = async (input) => {
    if (!canCalculate()) {
      setShowUpgradePrompt(true);
      return;
    }

    try {
      const newChat = await sendMessage(input, false, null, null);
      if (newChat) {
        setActiveChat(newChat);
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleOpenChat = (chat) => {
    // Allow viewing chat for everyone (ChatView handles follow-up restrictions)
    setActiveChat(chat);
  };

  const handleDeleteChat = async (chatId) => {
    await deleteChat(chatId);
  };

  // Show chat view if there's an active chat
  if (activeChat) {
    return <ChatView />;
  }

  const materials = ['concrete', 'drywall', 'paint', 'flooring', 'lumber', 'roofing'];

  const howItWorks = [
    {
      icon: Calculator,
      title: 'Describe Your Project',
      description: 'Tell us what you\'re building - dimensions, materials, or just a general idea.',
    },
    {
      icon: Zap,
      title: 'AI Calculates',
      description: 'Our AI instantly calculates exact quantities, waste factors, and costs.',
    },
    {
      icon: TrendingDown,
      title: 'Save Money & Time',
      description: 'Get a shopping list, avoid over-buying, and reduce waste on every project.',
    },
  ];

  const testimonials = [
    {
      name: 'Mike Johnson',
      role: 'General Contractor',
      content: 'This tool has saved me hours on every bid. The calculations are spot-on.',
      rating: 5,
    },
    {
      name: 'Sarah Chen',
      role: 'DIY Homeowner',
      content: 'Finally figured out exactly how much drywall I needed. No more return trips!',
      rating: 5,
    },
    {
      name: 'David Martinez',
      role: 'Project Estimator',
      content: 'The AI understands construction. It\'s like having an expert on call 24/7.',
      rating: 5,
    },
  ];

  return (
    <>
      <Helmet>
        <title>CalcBuildAI - AI-Powered Construction Material Calculator</title>
        <meta 
          name="description" 
          content="Calculate construction materials with AI. Get accurate estimates for concrete, drywall, paint, flooring, lumber, and roofing. Smart estimates for smarter builds." 
        />
        <meta property="og:title" content="CalcBuildAI - AI-Powered Construction Material Calculator" />
        <meta property="og:description" content="Calculate construction materials with AI. Save time, reduce waste, get accurate estimates instantly." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section with Animations */}
        <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-white to-white py-16 md:py-24">
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
            <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
            <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-4000"></div>
            
            {/* Floating construction icons */}
            <div className="hidden lg:block absolute top-32 left-[15%] animate-float opacity-20">
              <Ruler className="w-12 h-12 text-blue-600" />
            </div>
            <div className="hidden lg:block absolute top-48 right-[12%] animate-float animation-delay-2000 opacity-20">
              <PaintBucket className="w-10 h-10 text-indigo-600" />
            </div>
            <div className="hidden lg:block absolute bottom-32 left-[20%] animate-float animation-delay-4000 opacity-20">
              <Hammer className="w-8 h-8 text-purple-600" />
            </div>
            <div className="hidden lg:block absolute bottom-48 right-[18%] animate-float animation-delay-1000 opacity-20">
              <Home className="w-10 h-10 text-blue-500" />
            </div>
          </div>

          <div className="relative max-w-4xl mx-auto px-4 text-center">
            {/* Welcome message for logged in users */}
            {user && (
              <div className={`mb-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm ${hasAnimated ? '' : 'animate-fade-in'}`}>
                <span>👋</span>
                <span>Welcome back{user.email ? `, ${user.email.split('@')[0]}` : ''}!</span>
              </div>
            )}

            {/* Badge */}
            {!user && (
              <div className={`mb-6 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 rounded-full text-sm font-medium ${hasAnimated ? '' : 'animate-fade-in'}`}>
                <Sparkles className="w-4 h-4" />
                <span>AI-Powered Construction Calculator</span>
              </div>
            )}

            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight ${hasAnimated ? '' : 'animate-slide-up'}`}>
              Calculate Construction Materials{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                with AI
              </span>
            </h1>
            
            <p className={`text-xl text-gray-600 mb-8 max-w-2xl mx-auto ${hasAnimated ? '' : 'animate-slide-up animation-delay-200'}`}>
              Save time, reduce waste, get accurate estimates instantly. 
              Just describe your project and let AI do the math.
            </p>

            {/* Chat Input with sticky positioning on mobile */}
            <div className={`sticky bottom-0 z-30 md:static md:z-auto pb-0 md:pb-0 mb-8 md:mb-8 ${hasAnimated ? '' : 'animate-slide-up animation-delay-400'}`}>
              <div className="max-w-2xl mx-auto px-2 md:px-0">
                <div className="relative md:block">
                  {/* Glow effect - hidden on mobile when sticky */}
                  <div className="hidden md:absolute md:-inset-1 md:bg-gradient-to-r md:from-blue-600 md:to-indigo-600 md:rounded-2xl md:blur md:opacity-20 group-hover:md:opacity-30 transition duration-1000"></div>
                  <div className="relative">
                    <ChatInput
                      onSend={handleSendMessage}
                      isLoading={isLoading}
                      placeholder="What are you building today?"
                      examples={[
                        '10x12 patio, 4 inches thick',
                        'Drywall for 12x14 room with 9ft ceilings',
                        'Paint 3 bedrooms, 12x12 each',
                        'Deck 16x20 with stairs',
                      ]}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Usage indicator with animation */}
            <div className={`flex justify-center ${hasAnimated ? '' : 'animate-fade-in animation-delay-600'}`}>
              {isPro ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-lg shadow-blue-500/25">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">✨ Pro - Unlimited calculations</span>
                </div>
              ) : remainingCalcs > 0 ? (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">🎁 {remainingCalcs} free calculation{remainingCalcs !== 1 ? 's' : ''} remaining</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg border border-red-200">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">You've used all free calculations</span>
                  <button 
                    onClick={() => navigate('/pricing')}
                    className="ml-2 underline hover:no-underline font-semibold"
                  >
                    Upgrade
                  </button>
                </div>
              )}
            </div>

            {/* Trust Signals */}
            <div className={`mt-12 flex flex-wrap justify-center gap-4 max-w-2xl mx-auto ${hasAnimated ? '' : 'animate-fade-in animation-delay-800'}`}>
              {trustSignals.map((signal, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm rounded-full border border-gray-100 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
                >
                  <signal.icon className={`w-5 h-5 ${signal.color}`} />
                  <span className="text-sm font-medium text-gray-700">{signal.text}</span>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className={`mt-8 flex flex-wrap justify-center gap-6 text-sm text-gray-500 ${hasAnimated ? '' : 'animate-fade-in animation-delay-1000'}`}>
              <div className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                <Shield className="w-5 h-5 text-green-500" />
                <span>Secure & Private</span>
              </div>
              <div className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                <Clock className="w-5 h-5 text-blue-500" />
                <span>Results in seconds</span>
              </div>
              <div className="flex items-center gap-2 hover:text-gray-700 transition-colors">
                <Users className="w-5 h-5 text-purple-500" />
                <span>Trusted by pros</span>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Calculations (for logged-in users with chats) */}
        {user && chats.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Your Recent Calculations
                </h2>
                <span className="text-sm text-gray-500">
                  {chats.length} calculation{chats.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="space-y-4">
                {isLoading && chats.length === 0 ? (
                  <>
                    <ChatCardSkeleton />
                    <ChatCardSkeleton />
                  </>
                ) : (
                  chats.slice(0, 5).map((chat, idx) => (
                    <div 
                      key={chat.id} 
                      className={hasAnimated ? '' : 'animate-slide-up'}
                      style={hasAnimated ? {} : { animationDelay: `${idx * 100}ms` }}
                    >
                      <ChatCard
                        chat={chat}
                        onOpen={handleOpenChat}
                        onDelete={handleDeleteChat}
                      />
                    </div>
                  ))
                )}
              </div>

              {chats.length > 5 && (
                <div className="text-center mt-6">
                  <button className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-1 group">
                    View all calculations 
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Anonymous user recent chats */}
        {!user && chats.length > 0 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Your Recent Calculations
              </h2>
              
              <div className="space-y-4">
                {chats.map((chat) => (
                  <ChatCard
                    key={chat.id}
                    chat={chat}
                    onOpen={handleOpenChat}
                  />
                ))}
              </div>

              <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl text-center border border-blue-100">
                <p className="text-blue-800 mb-4">
                  <strong>Sign up free</strong> to save your calculations and continue conversations
                </p>
                <button
                  onClick={() => navigate('/signup')}
                  className="px-6 py-3 bg-gradient-to-r from-brand-600 to-accent-600 text-white rounded-lg hover:from-brand-700 hover:to-accent-700 font-semibold shadow-md hover:shadow-xl hover:shadow-brand-500/25 transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/30 active:scale-[0.98]"
                  aria-label="Create free account"
                >
                  Create Free Account
                </button>
              </div>
            </div>
          </section>
        )}

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
                Simple & Fast
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                How It Works
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Get accurate material estimates in seconds, not hours
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((step, idx) => (
                <div 
                  key={idx} 
                  className="text-center group relative"
                >
                  <div className="relative">
                    {/* Animated ring on hover */}
                    <div className="absolute inset-0 w-20 h-20 mx-auto rounded-2xl bg-blue-600 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:scale-110 transition-all duration-300 shadow-lg shadow-blue-100 group-hover:shadow-blue-300">
                      <step.icon className="w-10 h-10 text-blue-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    {idx < howItWorks.length - 1 && (
                      <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-blue-300 to-transparent"></div>
                    )}
                  </div>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold mb-3">
                    {idx + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Learn more link */}
            <div className="text-center mt-10">
              <Link 
                to="/how-it-works" 
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium group"
              >
                Learn more about how it works
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>

        {/* Material Types */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-4">
                6 Material Types
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Calculate Any Material
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                Specialized calculators for every type of construction material
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {materials.map((material, idx) => (
                <div 
                  key={material}
                  className="card-hover"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <MaterialCard type={material} />
                </div>
              ))}
            </div>
            
            {/* Calculator CTA */}
            <div className="mt-12 text-center p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Not sure which calculator to use?</h3>
              <p className="text-gray-600 mb-4">Just describe your project and our AI will figure it out!</p>
              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-brand-600 to-accent-600 text-white rounded-lg font-semibold hover:from-brand-700 hover:to-accent-700 transition-all shadow-md hover:shadow-xl hover:shadow-brand-500/25 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-600/30 active:scale-[0.98]"
                aria-label="Try the AI calculator"
              >
                <Calculator className="w-5 h-5" aria-hidden="true" />
                Try the AI Calculator
              </button>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 bg-white overflow-hidden">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-4">
                Real Reviews
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Trusted by Builders
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto text-lg">
                See what contractors and DIYers are saying
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, idx) => (
                <div 
                  key={idx} 
                  className="relative bg-white rounded-2xl p-6 card-hover border border-gray-100 shadow-sm"
                >
                  {/* Quote mark decoration */}
                  <div className="absolute -top-3 -left-2 text-6xl text-blue-100 font-serif leading-none">"</div>
                  
                  <div className="relative">
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/30">
                        {testimonial.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-500">{testimonial.role}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-600 relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="1"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }}></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Start Calculating?
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              Join thousands of builders saving time and reducing waste
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-white text-brand-700 rounded-xl font-semibold hover:bg-brand-50 hover:shadow-xl transition-all flex items-center justify-center gap-2 shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-[0.98]"
                aria-label="Scroll to calculator"
              >
                Start Calculating Free
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="px-8 py-4 bg-white/90 text-brand-700 rounded-xl font-semibold hover:bg-white transition-all border-2 border-white shadow-md hover:shadow-lg focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30 active:scale-[0.98]"
                aria-label="View pricing plans"
              >
                View Pricing
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        reason="limit"
      />
    </>
  );
};

export default HomePage;
